import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import VoiceDictationInput from '../../components/VoiceDictationInput';
import { FileText, Save, ArrowLeft, Printer, Download, Send } from 'lucide-react';
import api from '../../api';
import { SendForSignatureModal } from '../../components/SendForSignatureModal';
import { poc485FormToCms } from '../../utils/documentExport';
import { useAutoGrow } from '../../hooks/useAutoGrow';

const POC485FormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const formId = searchParams.get('id');
  
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({
    // Header Information
    patientHIClaimNo: '',
    startOfCareDate: '',
    certificationPeriodFrom: '',
    certificationPeriodTo: '',
    medicalRecordNo: '',
    providerNo: '',
    
    // Section 6 - Patient Info
    patientName: '',
    patientAddress: '',
    patientCity: '',
    patientState: '',
    patientZip: '',
    
    // Section 7 - Provider Info
    providerName: '',
    providerAddress: '',
    providerCity: '',
    providerState: '',
    providerZip: '',
    providerPhone: '',
    
    // Section 8-9 - Demographics
    dateOfBirth: '',
    sex: '',
    
    // Section 10 - Medications
    medications: '',
    
    // Section 11-13 - Diagnoses
    principalDiagnosis: '',
    principalDiagnosisDate: '',
    surgicalProcedure: '',
    surgicalProcedureDate: '',
    otherDiagnoses: '',
    otherDiagnosesDate: '',
    
    // Section 14-17
    dmeAndSupplies: '',
    safetyMeasures: '',
    nutritionalReq: '',
    allergies: '',
    
    // Section 18A - Functional Limitations
    functionalLimitations: {
      amputation: false,
      bowelBladder: false,
      contracture: false,
      hearing: false,
      paralysis: false,
      endurance: false,
      ambulation: false,
      speech: false,
      legallyBlind: false,
      dyspnea: false,
      other: false,
      otherSpecify: ''
    },
    
    // Section 18B - Activities Permitted
    activitiesPermitted: {
      completeBedridden: false,
      bedrestBRP: false,
      upAsToleratedPartial: false,
      weightBearing: false,
      transferBedChair: false,
      exercisesPrescribed: false,
      partialWeightBearing: false,
      independentAtHome: false,
      crutches: false,
      cane: false,
      wheelchair: false,
      walker: false,
      noRestrictions: false,
      other: false,
      otherSpecify: ''
    },
    
    // Section 19 - Mental Status
    mentalStatus: {
      oriented: false,
      comatose: false,
      forgetful: false,
      depressed: false,
      disoriented: false,
      lethargic: false,
      agitated: false,
      other: false,
      otherSpecify: ''
    },
    
    // Section 20 - Prognosis
    prognosis: '',
    
    // Section 21 - Orders for Discipline and Treatments
    ordersForDiscipline: '',
    
    // Section 22 - Goals/Rehabilitation Potential/Discharge Plans
    goalsRehabDischarge: '',
    
    // Section 23 - Nurse's Signature
    nurseSignature: '',
    nurseSignatureDate: '',
    verbalSOCDate: '',
    
    // Section 25 - Date HHA Received Signed POT
    dateHHAReceivedPOT: '',
    
    // Section 24 - Physician Info
    physicianName: '',
    physicianAddress: '',
    
    // Section 27 - Attending Physician Signature
    attendingPhysicianSignature: '',
    attendingPhysicianSignatureDate: ''
  });

  useEffect(() => {
    fetchPatients();
    if (formId) {
      fetchFormData();
    } else if (patientId) {
      // Open the patient's latest saved 485 (e.g. an auto-filled draft); otherwise start from auto-populated data
      api.get(`/poc485/patient/${patientId}`).then(r => {
        const latest = (r.data.pocs || []).sort((a, b) => String(b.updatedAt || b.createdAt || '').localeCompare(String(a.updatedAt || a.createdAt || '')))[0];
        if (latest) navigate(`/dashboard/clinical/poc-485/edit?id=${latest.id}`, { replace: true });
        else fetchPatients(true).then(fetchAutoPopulated485);
      }).catch(() => fetchPatients(true).then(fetchAutoPopulated485));
    }
  }, [formId, patientId]);

  const fetchPatients = async (prefill = false) => {
    try {
      const response = await api.get('/patients');
      if (response.data.success) {
        setPatients(response.data.patients);
        
        // Pre-fill only for a brand-new 485 (never over a saved draft)
        if (prefill && patientId) {
          const patient = response.data.patients.find(p => p.id === patientId);
          if (patient) {
            prefillFromPatient(patient);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  // Pre-fill form from patient data (basic info)
  const prefillFromPatient = (patient) => {
    setFormData(prev => ({
      ...prev,
      patientId: prev.patientId || patient.id,
      patientName: `${patient.lastName || ''}, ${patient.firstName || ''}`,
      patientAddress: patient.address || '',
      patientCity: patient.city || '',
      patientState: patient.state || '',
      patientZip: patient.zipCode || '',
      dateOfBirth: patient.dateOfBirth || '',
      sex: (patient.gender || '').trim().charAt(0).toUpperCase(),
      medicalRecordNo: patient.medicalRecordNumber || patient.id?.slice(0, 8) || '',
      // Insurance info
      patientHIClaimNo: patient.medicareNumber || patient.insuranceMemberId || '',
      // Provider info from patient's physician
      physicianName: patient.primaryPhysician || '',
      physicianAddress: patient.primaryPhysicianAddress || '',
      // Clinical info
      principalDiagnosis: patient.primaryDiagnosis || '',
      allergies: Array.isArray(patient.allergies) ? patient.allergies.join(', ') : (patient.allergies || ''),
      // Medications
      medications: formatMedications(patient.currentMedications),
      // Certification period
      certificationPeriodFrom: (patient.startOfCareDate || String(patient.admissionDate || '')).slice(0, 10) || new Date().toISOString().split('T')[0],
      certificationPeriodTo: getEndOfCertPeriod(patient.startOfCareDate || String(patient.admissionDate || '').slice(0, 10)),
      startOfCareDate: patient.startOfCareDate || String(patient.admissionDate || '').slice(0, 10)
    }));
  };

  // Format medications for display
  const formatMedications = (meds) => {
    if (!meds) return '';
    if (typeof meds === 'string') return meds;
    if (Array.isArray(meds)) {
      return meds.map(med => {
        if (typeof med === 'string') return med;
        return `${med.name || ''} ${med.dosage || ''} ${med.frequency || ''} ${med.route || ''}`.trim();
      }).join('\n');
    }
    return '';
  };

  // Calculate end of certification period (60 days from start)
  const getEndOfCertPeriod = (startDate) => {
    if (!startDate) {
      const start = new Date();
      start.setDate(start.getDate() + 60);
      return start.toISOString().split('T')[0];
    }
    const start = new Date(startDate);
    start.setDate(start.getDate() + 60);
    return start.toISOString().split('T')[0];
  };

  // Fetch auto-populated 485 data from Global Upload
  const fetchAutoPopulated485 = async () => {
    try {
      const response = await api.get(`/forms/poc-485/auto-populated/${patientId}`);
      if (response.data.success && response.data.form) {
        const form = response.data.form;
        toast.success('Auto-populated 485 data loaded!');
        
        // Merge auto-populated data into form
        setFormData(prev => ({
          ...prev,
          patientName: form.patientName || prev.patientName,
          certificationPeriodFrom: form.certPeriodStart || prev.certificationPeriodFrom,
          certificationPeriodTo: form.certPeriodEnd || prev.certificationPeriodTo,
          physicianName: form.physician || prev.physicianName,
          principalDiagnosis: form.primaryDiagnosis || prev.principalDiagnosis,
          // Load formData fields if present (nested checkbox groups are merged below)
          ...Object.fromEntries(Object.entries(form.formData || {}).filter(([k, v]) => v != null && !['functionalLimitations', 'activitiesPermitted', 'mentalStatus'].includes(k))),
          functionalLimitations: { ...prev.functionalLimitations, ...(typeof form.formData?.functionalLimitations === 'object' ? form.formData.functionalLimitations : {}) },
          activitiesPermitted: { ...prev.activitiesPermitted, ...(typeof form.formData?.activitiesPermitted === 'object' ? form.formData.activitiesPermitted : {}) },
          // Medications
          medications: form.formData?.medications ? formatMedications(form.formData.medications) : prev.medications,
          allergies: form.formData?.allergies ? (Array.isArray(form.formData.allergies) ? form.formData.allergies.join(', ') : form.formData.allergies) : prev.allergies,
          dmeAndSupplies: form.formData?.dmeSupplies ? (Array.isArray(form.formData.dmeSupplies) ? form.formData.dmeSupplies.join(', ') : form.formData.dmeSupplies) : prev.dmeAndSupplies,
          goalsRehabDischarge: form.formData?.dischargePlans || prev.goalsRehabDischarge,
          ordersForDiscipline: form.formData?.skilledNursingOrders || prev.ordersForDiscipline,
          // Mental status from cognitive status
          mentalStatus: {
            ...prev.mentalStatus,
            oriented: /oriented/i.test(String(form.formData?.mentalStatus || form.formData?.cognitiveStatus || ''))
          },
          // Secondary diagnoses
          otherDiagnoses: form.secondaryDiagnoses ? (Array.isArray(form.secondaryDiagnoses) ? form.secondaryDiagnoses.join(', ') : form.secondaryDiagnoses) : prev.otherDiagnoses,
          medicalRecordNo: prev.medicalRecordNo || form.formData?.medicalRecordNo || '',  // the chart's agency MR# wins
        }));
      }
    } catch (error) {
      console.error('Error fetching auto-populated 485:', error);
      // Not a critical error - just means no auto-populated data exists
    }
  };

  const fetchFormData = async () => {
    try {
      const response = await api.get(`/forms/poc-485/${formId}`);
      if (response.data.success) {
        const f = response.data.form;
        const chart = f.patientId ? await api.get(`/patients/${f.patientId}`).then(r => r.data.patient || r.data).catch(() => null) : null;
        setFormData(prev => ({
          ...prev, ...f,
          medicalRecordNo: chart?.medicalRecordNumber || prev.medicalRecordNo || f.medicalRecordNo || '',
          functionalLimitations: { ...prev.functionalLimitations, ...(f.functionalLimitations || {}) },
          activitiesPermitted: { ...prev.activitiesPermitted, ...(f.activitiesPermitted || {}) },
          mentalStatus: { ...prev.mentalStatus, ...(f.mentalStatus || {}) },
        }));
      }
    } catch (error) {
      console.error('Error fetching form:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = formId ? `/forms/poc-485/${formId}` : '/forms/poc-485';
      const method = formId ? 'put' : 'post';
      
      const response = await api[method](endpoint, formData);
      if (response.data.success) {
        toast.success('Form saved successfully');
        if (!formId) {
          navigate(`/dashboard/clinical/poc-485/form?id=${response.data.form.id}`);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const signPatientId = formData.patientId || patientId;
  const [signing, setSigning] = useState(null);
  const openSignature = async () => {
    if (!(await handleSave())) return;
    const p = patients.find(x => x.id === signPatientId) || {};
    setSigning({ patientId: signPatientId, patientName: `${p.firstName || ''} ${p.lastName || ''}`.trim() || formData.patientName, chart: p });
  };

  const CheckboxField = ({ label, checked, onChange, className = '' }) => (
    <label className={`flex items-center gap-2 text-sm cursor-pointer ${className}`}>
      <input
        type="checkbox"
        checked={checked || false}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300"
      />
      <span>{label}</span>
    </label>
  );

  const growRef = useAutoGrow(formData);
  return (
    <DashboardLayout>
      <div className="p-4 bg-gray-100 min-h-screen" ref={growRef}>
        {/* Header */}
        <div className="bg-white border-b mb-4 p-3 flex justify-between items-center rounded shadow-sm">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-800">CMS-485 Form</h1>
              <p className="text-xs text-gray-500">Home Health Certification and Plan of Care</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#3498db] hover:bg-[#2980b9]" data-testid="poc485-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
            {formData.status === 'Signed' && (
              <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-semibold" data-testid="poc485-signed-badge">Signed by physician (locked)</span>
            )}
            {formId && signPatientId && formData.status !== 'Signed' && (
              <Button size="sm" onClick={openSignature} disabled={saving} data-testid="poc485-send-signature"
                className="bg-gray-900 text-[#d4af37] hover:bg-gray-800">
                <Send className="w-4 h-4 mr-1" /> Send for Physician Signature
              </Button>
            )}
          </div>
        </div>

        {/* Form Container - CMS-485 Layout */}
        <div className="bg-white border rounded shadow-sm max-w-5xl mx-auto text-sm">
          {/* Form Header */}
          <div className="border-b p-2 flex justify-between">
            <div className="text-xs">
              <div className="font-semibold">Department of Health and Human Services</div>
              <div>Centers for Medicare & Medicaid Services</div>
            </div>
            <div className="text-xs text-right">
              <div>Form Approved</div>
              <div>OMB No. 0938-0357</div>
            </div>
          </div>

          {/* Title */}
          <div className="bg-gray-100 border-b p-2 text-center font-bold">
            HOME HEALTH CERTIFICATION AND PLAN OF CARE
          </div>

          {/* Row 1: Patient HI Claim, SOC Date, Cert Period, Medical Record, Provider No */}
          <div className="grid grid-cols-5 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">1. Patient's HI Claim No.</div>
              <VoiceDictationInput
                value={formData.patientHIClaimNo}
                onChange={(e) => handleInputChange('patientHIClaimNo', e.target.value)}
                className="w-full text-sm"
              />
            </div>
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">2. Start Of Care Date</div>
              <input
                type="date"
                value={formData.startOfCareDate}
                onChange={(e) => handleInputChange('startOfCareDate', e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">3. Certification Period</div>
              <div className="flex gap-2 text-xs">
                <div>
                  <span>From:</span>
                  <input
                    type="date"
                    value={formData.certificationPeriodFrom}
                    onChange={(e) => handleInputChange('certificationPeriodFrom', e.target.value)}
                    className="w-full border rounded px-1 py-0.5 text-xs"
                  />
                </div>
                <div>
                  <span>To:</span>
                  <input
                    type="date"
                    value={formData.certificationPeriodTo}
                    onChange={(e) => handleInputChange('certificationPeriodTo', e.target.value)}
                    className="w-full border rounded px-1 py-0.5 text-xs"
                  />
                </div>
              </div>
            </div>
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">4. Medical Record No.</div>
              <VoiceDictationInput
                value={formData.medicalRecordNo}
                onChange={(e) => handleInputChange('medicalRecordNo', e.target.value)}
                className="w-full text-sm"
              />
            </div>
            <div className="p-2">
              <div className="text-xs font-semibold mb-1">5. Provider No.</div>
              <VoiceDictationInput
                value={formData.providerNo}
                onChange={(e) => handleInputChange('providerNo', e.target.value)}
                className="w-full text-sm"
              />
            </div>
          </div>

          {/* Row 2: Patient Name/Address | Provider Name/Address */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">6. Patient's Name and Address</div>
              <VoiceDictationInput
                value={formData.patientName}
                onChange={(e) => handleInputChange('patientName', e.target.value)}
                placeholder="Patient Name"
                className="w-full mb-1 text-sm"
              />
              <VoiceDictationInput
                value={formData.patientAddress}
                onChange={(e) => handleInputChange('patientAddress', e.target.value)}
                placeholder="Address, City, State, ZIP"
                className="w-full text-sm"
                rows={2}
              />
            </div>
            <div className="p-2">
              <div className="text-xs font-semibold mb-1">7. Provider's Name, Address and Telephone Number</div>
              <VoiceDictationInput
                value={formData.providerName}
                onChange={(e) => handleInputChange('providerName', e.target.value)}
                placeholder="Provider Name"
                className="w-full mb-1 text-sm"
              />
              <VoiceDictationInput
                value={formData.providerAddress}
                onChange={(e) => handleInputChange('providerAddress', e.target.value)}
                placeholder="Address, Phone"
                className="w-full text-sm"
                rows={2}
              />
            </div>
          </div>

          {/* Row 3: DOB, Sex | Medications */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r">
              <div className="grid grid-cols-3">
                <div className="border-r p-2 col-span-2">
                  <div className="text-xs font-semibold mb-1">8. Date of Birth</div>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold mb-1">9. Sex</div>
                  <div className="flex gap-2">
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={formData.sex === 'M'}
                        onChange={() => handleInputChange('sex', 'M')}
                        className="w-3 h-3"
                      />
                      <span className="text-xs">M</span>
                    </label>
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={formData.sex === 'F'}
                        onChange={() => handleInputChange('sex', 'F')}
                        className="w-3 h-3"
                      />
                      <span className="text-xs">F</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-2 bg-blue-50">
              <div className="text-xs font-semibold mb-1">10. Medications: Dose/Frequency/Route (N)ew (C)hanged</div>
              <VoiceDictationInput
                value={formData.medications}
                onChange={(e) => handleInputChange('medications', e.target.value)}
                rows={3}
                className="w-full text-sm bg-white"
              />
            </div>
          </div>

          {/* Row 4: Diagnoses | Medications continued */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r">
              {/* Principal Diagnosis */}
              <div className="grid grid-cols-3 border-b">
                <div className="border-r p-2">
                  <div className="text-xs font-semibold">11. ICD-9-CM</div>
                </div>
                <div className="border-r p-2">
                  <div className="text-xs font-semibold">Principal Diagnosis</div>
                  <VoiceDictationInput
                    value={formData.principalDiagnosis}
                    onChange={(e) => handleInputChange('principalDiagnosis', e.target.value)}
                    className="w-full text-xs"
                  />
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold">Date</div>
                  <input
                    type="date"
                    value={formData.principalDiagnosisDate}
                    onChange={(e) => handleInputChange('principalDiagnosisDate', e.target.value)}
                    className="w-full border rounded px-1 py-0.5 text-xs"
                  />
                </div>
              </div>
              {/* Surgical Procedure */}
              <div className="grid grid-cols-3 border-b">
                <div className="border-r p-2">
                  <div className="text-xs font-semibold">12. ICD-9-CM</div>
                </div>
                <div className="border-r p-2">
                  <div className="text-xs font-semibold">Surgical Procedure</div>
                  <VoiceDictationInput
                    value={formData.surgicalProcedure}
                    onChange={(e) => handleInputChange('surgicalProcedure', e.target.value)}
                    className="w-full text-xs"
                  />
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold">Date</div>
                  <input
                    type="date"
                    value={formData.surgicalProcedureDate}
                    onChange={(e) => handleInputChange('surgicalProcedureDate', e.target.value)}
                    className="w-full border rounded px-1 py-0.5 text-xs"
                  />
                </div>
              </div>
              {/* Other Diagnoses */}
              <div className="grid grid-cols-3">
                <div className="border-r p-2">
                  <div className="text-xs font-semibold">13. ICD-9-CM</div>
                </div>
                <div className="border-r p-2">
                  <div className="text-xs font-semibold">Other Pertinent Diagnoses</div>
                  <VoiceDictationInput
                    value={formData.otherDiagnoses}
                    onChange={(e) => handleInputChange('otherDiagnoses', e.target.value)}
                    className="w-full text-xs"
                    rows={2}
                  />
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold">Date</div>
                  <input
                    type="date"
                    value={formData.otherDiagnosesDate}
                    onChange={(e) => handleInputChange('otherDiagnosesDate', e.target.value)}
                    className="w-full border rounded px-1 py-0.5 text-xs"
                  />
                </div>
              </div>
            </div>
            <div className="p-2 bg-blue-50">
              {/* Medications continued area */}
              <VoiceDictationInput
                value={formData.medications}
                onChange={(e) => handleInputChange('medications', e.target.value)}
                rows={6}
                className="w-full text-sm bg-white"
                placeholder="Continue medications..."
              />
            </div>
          </div>

          {/* Row 5: DME/Supplies | Safety Measures */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">14. DME and Supplies</div>
              <VoiceDictationInput
                value={formData.dmeAndSupplies}
                onChange={(e) => handleInputChange('dmeAndSupplies', e.target.value)}
                className="w-full text-sm"
              />
            </div>
            <div className="p-2">
              <div className="text-xs font-semibold mb-1">15. Safety Measures</div>
              <VoiceDictationInput
                value={formData.safetyMeasures}
                onChange={(e) => handleInputChange('safetyMeasures', e.target.value)}
                className="w-full text-sm"
              />
            </div>
          </div>

          {/* Row 6: Nutritional Req | Allergies */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">16. Nutritional Req</div>
              <VoiceDictationInput
                value={formData.nutritionalReq}
                onChange={(e) => handleInputChange('nutritionalReq', e.target.value)}
                className="w-full text-sm"
              />
            </div>
            <div className="p-2">
              <div className="text-xs font-semibold mb-1">17. Allergies</div>
              <VoiceDictationInput
                value={formData.allergies}
                onChange={(e) => handleInputChange('allergies', e.target.value)}
                className="w-full text-sm"
              />
            </div>
          </div>

          {/* Row 7: Functional Limitations | Activities Permitted */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-2">18.A. Functional Limitations</div>
              <div className="grid grid-cols-3 gap-1 text-xs">
                <CheckboxField label="1. Amputation" checked={formData.functionalLimitations.amputation} onChange={(v) => handleCheckboxChange('functionalLimitations', 'amputation', v)} />
                <CheckboxField label="5. Paralysis" checked={formData.functionalLimitations.paralysis} onChange={(v) => handleCheckboxChange('functionalLimitations', 'paralysis', v)} />
                <CheckboxField label="9. Legally Blind" checked={formData.functionalLimitations.legallyBlind} onChange={(v) => handleCheckboxChange('functionalLimitations', 'legallyBlind', v)} />
                <CheckboxField label="2. Bowel/Bladder" checked={formData.functionalLimitations.bowelBladder} onChange={(v) => handleCheckboxChange('functionalLimitations', 'bowelBladder', v)} />
                <CheckboxField label="6. Endurance" checked={formData.functionalLimitations.endurance} onChange={(v) => handleCheckboxChange('functionalLimitations', 'endurance', v)} />
                <CheckboxField label="10. Dyspnea" checked={formData.functionalLimitations.dyspnea} onChange={(v) => handleCheckboxChange('functionalLimitations', 'dyspnea', v)} />
                <CheckboxField label="3. Contracture" checked={formData.functionalLimitations.contracture} onChange={(v) => handleCheckboxChange('functionalLimitations', 'contracture', v)} />
                <CheckboxField label="7. Ambulation" checked={formData.functionalLimitations.ambulation} onChange={(v) => handleCheckboxChange('functionalLimitations', 'ambulation', v)} />
                <CheckboxField label="A. Other" checked={formData.functionalLimitations.other} onChange={(v) => handleCheckboxChange('functionalLimitations', 'other', v)} />
                <CheckboxField label="4. Hearing" checked={formData.functionalLimitations.hearing} onChange={(v) => handleCheckboxChange('functionalLimitations', 'hearing', v)} />
                <CheckboxField label="8. Speech" checked={formData.functionalLimitations.speech} onChange={(v) => handleCheckboxChange('functionalLimitations', 'speech', v)} />
              </div>
            </div>
            <div className="p-2">
              <div className="text-xs font-semibold mb-2">18.B. Activities Permitted</div>
              <div className="grid grid-cols-3 gap-1 text-xs">
                <CheckboxField label="1. Complete Bedridden" checked={formData.activitiesPermitted.completeBedridden} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'completeBedridden', v)} />
                <CheckboxField label="5. Partial Weight Bearing" checked={formData.activitiesPermitted.partialWeightBearing} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'partialWeightBearing', v)} />
                <CheckboxField label="A. Wheelchair" checked={formData.activitiesPermitted.wheelchair} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'wheelchair', v)} />
                <CheckboxField label="2. Bedrest BRP" checked={formData.activitiesPermitted.bedrestBRP} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'bedrestBRP', v)} />
                <CheckboxField label="6. Independent At Home" checked={formData.activitiesPermitted.independentAtHome} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'independentAtHome', v)} />
                <CheckboxField label="B. Walker" checked={formData.activitiesPermitted.walker} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'walker', v)} />
                <CheckboxField label="3. Up As Tolerated" checked={formData.activitiesPermitted.upAsToleratedPartial} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'upAsToleratedPartial', v)} />
                <CheckboxField label="7. Crutches" checked={formData.activitiesPermitted.crutches} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'crutches', v)} />
                <CheckboxField label="C. No Restrictions" checked={formData.activitiesPermitted.noRestrictions} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'noRestrictions', v)} />
                <CheckboxField label="4. Transfer Bed/Chair" checked={formData.activitiesPermitted.transferBedChair} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'transferBedChair', v)} />
                <CheckboxField label="8. Cane" checked={formData.activitiesPermitted.cane} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'cane', v)} />
                <CheckboxField label="D. Other" checked={formData.activitiesPermitted.other} onChange={(v) => handleCheckboxChange('activitiesPermitted', 'other', v)} />
              </div>
            </div>
          </div>

          {/* Row 8: Mental Status | Prognosis */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-2">19. Mental Status</div>
              <div className="grid grid-cols-4 gap-1 text-xs">
                <CheckboxField label="1. Oriented" checked={formData.mentalStatus.oriented} onChange={(v) => handleCheckboxChange('mentalStatus', 'oriented', v)} />
                <CheckboxField label="2. Comatose" checked={formData.mentalStatus.comatose} onChange={(v) => handleCheckboxChange('mentalStatus', 'comatose', v)} />
                <CheckboxField label="3. Forgetful" checked={formData.mentalStatus.forgetful} onChange={(v) => handleCheckboxChange('mentalStatus', 'forgetful', v)} />
                <CheckboxField label="4. Depressed" checked={formData.mentalStatus.depressed} onChange={(v) => handleCheckboxChange('mentalStatus', 'depressed', v)} />
                <CheckboxField label="5. Disoriented" checked={formData.mentalStatus.disoriented} onChange={(v) => handleCheckboxChange('mentalStatus', 'disoriented', v)} />
                <CheckboxField label="6. Lethargic" checked={formData.mentalStatus.lethargic} onChange={(v) => handleCheckboxChange('mentalStatus', 'lethargic', v)} />
                <CheckboxField label="7. Agitated" checked={formData.mentalStatus.agitated} onChange={(v) => handleCheckboxChange('mentalStatus', 'agitated', v)} />
                <CheckboxField label="8. Other" checked={formData.mentalStatus.other} onChange={(v) => handleCheckboxChange('mentalStatus', 'other', v)} />
              </div>
            </div>
            <div className="p-2">
              <div className="text-xs font-semibold mb-1">20. Prognosis</div>
              <div className="flex gap-4 text-xs">
                <label className="flex items-center gap-1">
                  <input type="radio" name="prognosis" value="Poor" checked={formData.prognosis === 'Poor'} onChange={(e) => handleInputChange('prognosis', e.target.value)} />
                  <span>Poor</span>
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" name="prognosis" value="Guarded" checked={formData.prognosis === 'Guarded'} onChange={(e) => handleInputChange('prognosis', e.target.value)} />
                  <span>Guarded</span>
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" name="prognosis" value="Fair" checked={formData.prognosis === 'Fair'} onChange={(e) => handleInputChange('prognosis', e.target.value)} />
                  <span>Fair</span>
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" name="prognosis" value="Good" checked={formData.prognosis === 'Good'} onChange={(e) => handleInputChange('prognosis', e.target.value)} />
                  <span>Good</span>
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" name="prognosis" value="Excellent" checked={formData.prognosis === 'Excellent'} onChange={(e) => handleInputChange('prognosis', e.target.value)} />
                  <span>Excellent</span>
                </label>
              </div>
            </div>
          </div>

          {/* Row 9: Orders for Discipline and Treatments */}
          <div className="border-b p-2">
            <div className="text-xs font-semibold mb-1">21. Orders for Discipline and Treatments (Specify Amount/Frequency/Duration)</div>
            <VoiceDictationInput
              value={formData.ordersForDiscipline}
              onChange={(e) => handleInputChange('ordersForDiscipline', e.target.value)}
              rows={4}
              className="w-full text-sm"
            />
          </div>

          {/* Row 10: Goals/Rehabilitation Potential/Discharge Plans */}
          <div className="border-b p-2">
            <div className="text-xs font-semibold mb-1">22. Goals/Rehabilitation Potential/Discharge Plans</div>
            <VoiceDictationInput
              value={formData.goalsRehabDischarge}
              onChange={(e) => handleInputChange('goalsRehabDischarge', e.target.value)}
              rows={4}
              className="w-full text-sm"
            />
          </div>

          {/* Row 11: Nurse Signature | Date HHA Received */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">23. Nurse's Signature and Date of Verbal SOC Where Applicable:</div>
              <div className="flex gap-2">
                <VoiceDictationInput
                  value={formData.nurseSignature}
                  onChange={(e) => handleInputChange('nurseSignature', e.target.value)}
                  placeholder="Signature"
                  className="flex-1 text-sm"
                />
                <input
                  type="date"
                  value={formData.nurseSignatureDate}
                  onChange={(e) => handleInputChange('nurseSignatureDate', e.target.value)}
                  className="w-32 border rounded px-2 py-1 text-sm"
                />
              </div>
            </div>
            <div className="p-2">
              <div className="text-xs font-semibold mb-1">25. Date HHA Received Signed POT</div>
              <input
                type="date"
                value={formData.dateHHAReceivedPOT}
                onChange={(e) => handleInputChange('dateHHAReceivedPOT', e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
          </div>

          {/* Row 12: Physician Name/Address | Certification Statement */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">24. Physician's Name and Address</div>
              <VoiceDictationInput
                value={formData.physicianName}
                onChange={(e) => handleInputChange('physicianName', e.target.value)}
                placeholder="Physician Name"
                className="w-full mb-1 text-sm"
              />
              <VoiceDictationInput
                value={formData.physicianAddress}
                onChange={(e) => handleInputChange('physicianAddress', e.target.value)}
                placeholder="Address"
                className="w-full text-sm"
              />
            </div>
            <div className="p-2 text-xs">
              <div className="font-semibold mb-1">26. I certify/recertify that this patient is confined to his/her home and needs intermittent skilled nursing care, physical therapy and/or speech therapy or continues to need occupational therapy. The patient is under my care, and I have authorized the services on this plan of care and will periodically review the plan.</div>
            </div>
          </div>

          {/* Row 13: Attending Physician Signature | Warning */}
          <div className="grid grid-cols-2 border-b">
            <div className="border-r p-2">
              <div className="text-xs font-semibold mb-1">27. Attending Physician's Signature and Date Signed</div>
              <div className="flex gap-2">
                <VoiceDictationInput
                  value={formData.attendingPhysicianSignature}
                  onChange={(e) => handleInputChange('attendingPhysicianSignature', e.target.value)}
                  placeholder="Signature"
                  className="flex-1 text-sm"
                />
                <input
                  type="date"
                  value={formData.attendingPhysicianSignatureDate}
                  onChange={(e) => handleInputChange('attendingPhysicianSignatureDate', e.target.value)}
                  className="w-32 border rounded px-2 py-1 text-sm"
                />
              </div>
            </div>
            <div className="p-2 text-xs">
              <div className="font-semibold">28. Anyone who misrepresents, falsifies, or conceals essential information required for payment of Federal funds may be subject to fine, imprisonment, or civil penalty under applicable Federal laws.</div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-2 text-xs text-gray-500 text-center border-t">
            Form CMS-485 (C-3) (02-94) (Formerly HCFA-485) (Print Aligned)
          </div>
        </div>
      </div>
      {signing && <SendForSignatureModal docData={{ data: poc485FormToCms(formData) }} patientContext={signing} sourceFormId={formId} onClose={() => setSigning(null)} />}
    </DashboardLayout>
  );
};

export default POC485FormPage;
